<template>
  <div class="mb-5">
    <div
      class="dropzone gc-rounded"
      :class="{
        'drag-over': dragOver,
        'has-files': value.length > 0,
      }"
      @drop.prevent="onDrop"
      @dragleave="onDragLeave"
      @dragover.prevent="onDragOver"
    >
      <div class="dropzone__file-input-wrap">
        <input
          ref="fileInput"
          :accept="accept"
          type="file"
          :multiple="multiple ? 'multiple' : false"
          @change="addFile"
        />
      </div>

      <div class="dropzone__visible-content">
        <div v-if="value.length" class="dropzone__files gc-p-4">
          <div v-for="(file, i) in value" :key="i" class="dropzone__file">
            <div class="gc-flex gc-justify-between gc-items-center">
              <div class="gc-flex align-center">
                <font-awesome-icon
                  :icon="getFileIcon(file)"
                  size="2x"
                  class="gc-mr-2 gc-flex-shrink-0 gc-text-primary"
                />
                <span class="text-caption gc-block">{{ file.name }}</span>
              </div>
              <gc-button
                class="dropzone__remove-file-button"
                icon
                @click.prevent="removeFile(i)"
                ><font-awesome-icon :icon="faTimes"
              /></gc-button>
            </div>
            <hr class="gc-my-2 gc-border-gray-100" />
          </div>
          <gc-button
            v-if="multiple"
            class="dropzone__add-file-button"
            small
            text
            block
            color="primary"
            @click.prevent="openFileChooser()"
            ><font-awesome-icon :icon="faUpload" class="gc-mr-2" />{{
              $t('upload.addMoreFiles')
            }}</gc-button
          >
        </div>
        <div
          v-else
          class="dropzone__placeholder text-body-2 gc-text-center gc-p-4"
          @click="openFileChooser"
        >
          <font-awesome-icon
            v-if="dragOver"
            size="2x"
            :icon="faFilePlus"
            class="gc-mb-2"
          />
          <font-awesome-icon
            v-else
            size="2x"
            :icon="faUpload"
            class="gc-mb-2 gc-mx-auto"
          />
          <span class="gc-block">{{
            dragOver
              ? $tc('upload.dropFiles', multiple ? 2 : 1)
              : isMobile
              ? $tc('upload.mobilePlaceholder', multiple ? 2 : 1)
              : $tc('upload.placeholder', multiple ? 2 : 1)
          }}</span>
        </div>
      </div>
    </div>
    <gc-alert v-if="errMsg" v-html="errMsg" type="error" class="gc-mt-1" text />
  </div>
</template>

<script lang="ts">
import { VInput } from 'vuetify/lib';
import { computed, ref } from '@vue/composition-api';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import {
  faFileAlt,
  faFileImage,
  faFilePdf,
  faFilePlus,
  faUpload,
} from '@fortawesome/pro-duotone-svg-icons';
import GcDivider from '@/components/primitives/GcDivider.vue';
import GcButton from '@/components/primitives/GcButton.vue';
import GcAlert from '@/components/primitives/GcAlert.vue';
import i18n from '@/plugins/i18n';

const getSizeInMB = (sizeInBytes: number): string =>
  (sizeInBytes / 1024 ** 2).toLocaleString('de-DE', {
    maximumFractionDigits: 1,
  });

export default VInput.extend({
  components: {
    GcAlert,
    GcButton,
    GcDivider,
  },
  props: {
    accept: {
      default: () => '',
      type: String,
    },
    maxFileSizeInBytes: {
      default: false,
      type: [Boolean, Number],
    },
    multiple: {
      default: false,
      type: Boolean,
    },
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setup: (props: any, context) => {
    const fileInput = ref<HTMLInputElement | null>(null);
    const dragOver = ref(false);
    const errMsg = ref('');
    const totalMaxSizeInBytes = Number(process.env.VUE_APP_MAX_TOTAL_BYTESIZE);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addFile = (e: any) => {
      let newFiles = e.dataTransfer?.files || e.target.files;

      if (!props.multiple && newFiles.length > 1) {
        newFiles = [newFiles[0]];
      }

      if (props.maxFileSizeInBytes) {
        const tooLargeFiles = [...newFiles].filter(
          (file) => file.size > props.maxFileSizeInBytes,
        );
        if (tooLargeFiles.length) {
          let text = (i18n.tc(
            'upload.fileSizeExceededWarning',
            tooLargeFiles.length,
            { size: getSizeInMB(props.maxFileSizeInBytes) },
          ) + '<br>') as string;

          text += tooLargeFiles
            .map((file) => `${file.name} (${getSizeInMB(file.size)} MB)`)
            .join('<br>');
          errMsg.value = text;
          return;
        }
      }

      if (props.accept) {
        const acceptMimeTypes = props.accept
          .split(',')
          .map((mimeType) => mimeType.trim());
        const invalidMimeTypes = [...newFiles].filter(
          (file) =>
            !acceptMimeTypes.includes(file.type) &&
            !acceptMimeTypes.includes(
              file.type.replace(/^([^/]+\/).+$/, '$1*'),
            ),
        );

        if (invalidMimeTypes.length) {
          let text = (i18n.tc(
            'upload.mimeTypeWarning',
            invalidMimeTypes.length,
          ) + '<br>') as string;

          text += invalidMimeTypes.map((file) => file.name).join('<br>');
          errMsg.value = text;
          return;
        }
      }

      if (newFiles.length) {
        const emittedValue = props.multiple
          ? [...props.value, ...newFiles]
          : [...newFiles];
        const totalFilesInBytes = emittedValue.reduce(
          (counter, file) => counter + file.size,
          0,
        );
        if (totalFilesInBytes > totalMaxSizeInBytes) {
          errMsg.value = i18n.tc(
            'upload.totalFileSizeExceededWarning',
            emittedValue.length,
            {
              maxSize: getSizeInMB(totalMaxSizeInBytes),
              difference: getSizeInMB(totalFilesInBytes - totalMaxSizeInBytes),
            },
          ) as string;
          return;
        }
        context.emit('input', emittedValue);
        errMsg.value = '';
      }
    };
    const removeFile = (fileIndex: number) => {
      if (props.value[fileIndex]) {
        const updatedArray = [...props.value];
        updatedArray.splice(fileIndex, 1);
        context.emit('input', updatedArray);
      }
    };
    const openFileChooser = () => {
      fileInput.value?.click();
      errMsg.value = '';
    };
    const onDrop = (e: DragEvent) => {
      dragOver.value = false;
      addFile(e);
    };
    const onDragOver = () => {
      dragOver.value = true;
    };
    const onDragLeave = () => {
      dragOver.value = false;
    };

    const getFileIcon = (file: File) => {
      if (file.type?.includes('pdf')) {
        return faFilePdf;
      } else if (file.type?.includes('image/')) {
        return faFileImage;
      } else {
        return faFileAlt;
      }
    };

    const isMobile = computed(
      () =>
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ) && navigator.mediaDevices.enumerateDevices,
    );

    return {
      addFile,
      dragOver,
      errMsg,
      faFilePlus,
      faTimes,
      faUpload,
      fileInput,
      getFileIcon,
      isMobile,
      onDragLeave,
      onDragOver,
      onDrop,
      openFileChooser,
      removeFile,
    };
  },
});
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/_colors.scss';

.dropzone {
  border: 2px dashed map-get($grey, lighten-2);

  &.has-files {
    border-style: solid;
  }

  &.drag-over {
    border-color: var(--v-primary-base) !important;

    .dropzone__placeholder.text--secondary {
      color: var(--v-primary-base) !important;
      font-weight: bold;
    }
  }

  &__file-input-wrap {
    overflow: hidden;
    height: 0;
  }

  &__visible-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100px;
  }

  &__placeholder {
    text-align: center;
  }

  &__file {
    width: auto;
    word-break: break-all;
  }
}
</style>
